import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { getEstudo } from "@/lib/estudo";
import { BancaGrid } from "@/components/BancaGrid";
import { toast } from "sonner";
import { Brain, Loader2, Sparkles, Lightbulb, CheckCircle2 } from "lucide-react";

const Select = ({ value, onChange, options, testid, placeholder, disabled }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
    className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50">
    <option value="Todas">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function MapasMentaisPage() {
  const navigate = useNavigate(); const { user } = useAuth();
  const [banca, setBanca] = useState(() => { const e = getEstudo(); return e.banca && e.banca !== "Todas" ? e.banca : "CEBRASPE"; });
  const [disciplina, setDisciplina] = useState("Todas");
  const [assunto, setAssunto] = useState("Todas");
  const [disciplinas, setDisciplinas] = useState([]);
  const [assuntos, setAssuntos] = useState([]);
  const [mapa, setMapa] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadOptions = useCallback((b, d) => {
    api.get("/mapas/options", { params: { banca: b, disciplina: d } })
      .then((r) => { setDisciplinas(r.data.disciplinas || []); setAssuntos(r.data.assuntos || []); })
      .catch(() => {});
  }, []);

  useEffect(() => { loadOptions(banca, "Todas"); }, [banca, loadOptions]);

  const onBanca = (b) => { setBanca(b); setDisciplina("Todas"); setAssunto("Todas"); setMapa(null); };
  const onDisciplina = (d) => { setDisciplina(d); setAssunto("Todas"); loadOptions(banca, d); };

  const gerar = async () => {
    if (!user?.is_premium) { navigate("/premium"); return; }
    if (disciplina === "Todas") { toast.error("Selecione uma disciplina."); return; }
    setLoading(true); setMapa(null);
    try {
      const { data } = await api.post("/ia/mindmap", {
        banca, disciplina, assunto: assunto === "Todas" ? null : assunto,
      });
      setMapa(data.mapa);
      if (data.cached) toast.info("Mapa reutilizado do cache (sem gasto de IA).");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Falha ao gerar o mapa.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="mapas-page" className="mx-auto max-w-4xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-purple-500/15 text-purple-400"><Brain className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-extrabold text-white">Mapas Mentais</h1>
          <p className="text-sm text-slate-400">Gerados sob demanda a partir dos dados reais do ApoiaConcursos.</p>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Banca</p>
        <BancaGrid value={banca} onSelect={onBanca} includeTodas={false} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={disciplina} onChange={onDisciplina} options={disciplinas} testid="mapas-disciplina" placeholder="Selecione a disciplina" />
        <Select value={assunto} onChange={setAssunto} options={assuntos} testid="mapas-assunto" placeholder="Assunto (opcional)" disabled={disciplina === "Todas"} />
        <button onClick={gerar} disabled={loading} data-testid="mapas-gerar"
          className="flex items-center gap-2 rounded-lg bg-purple-500 px-4 py-2 text-sm font-bold text-white hover:bg-purple-600 disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Gerar mapa mental
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 rounded-2xl border border-slate-800 bg-slate-900 p-8 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Montando seu mapa mental com base nos dados reais…
        </div>
      )}

      {mapa && (
        <div data-testid="mapas-resultado" className="rounded-2xl border border-slate-800 bg-slate-900 p-5 sm:p-6">
          <h2 className="text-xl font-extrabold text-white">{mapa.titulo}</h2>
          <p className="text-xs text-slate-500">{mapa.banca} · {mapa.disciplina}{mapa.assunto ? ` · ${mapa.assunto}` : ""} · {mapa.questoes_analisadas} questões analisadas</p>

          {mapa.sem_dados ? (
            <p className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-300">{mapa.observacao}</p>
          ) : (
            <>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {(mapa.ramos || []).map((r, i) => (
                  <div key={i} className="rounded-xl border border-slate-800 bg-slate-800/40 p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="grid h-6 w-6 place-items-center rounded-md bg-purple-500/20 text-xs font-bold text-purple-300">{i + 1}</span>
                      <h3 className="font-bold text-white">{r.titulo}</h3>
                    </div>
                    {(r.conceitos || []).length > 0 && (
                      <div className="mb-2">
                        <p className="flex items-center gap-1 text-[11px] font-bold uppercase text-emerald-400"><Lightbulb className="h-3 w-3" /> Conceitos</p>
                        <ul className="ml-1 mt-1 space-y-0.5 text-xs text-slate-300">{r.conceitos.map((c, j) => <li key={j}>• {c}</li>)}</ul>
                      </div>
                    )}
                    {(r.pontos_importantes || []).length > 0 && (
                      <div className="mb-2">
                        <p className="flex items-center gap-1 text-[11px] font-bold uppercase text-amber-400"><CheckCircle2 className="h-3 w-3" /> Pontos importantes</p>
                        <ul className="ml-1 mt-1 space-y-0.5 text-xs text-slate-300">{r.pontos_importantes.map((c, j) => <li key={j}>• {c}</li>)}</ul>
                      </div>
                    )}
                    {(r.subtopicos || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {r.subtopicos.map((s, j) => <span key={j} className="rounded-full bg-slate-700 px-2 py-0.5 text-[11px] text-slate-300">{s}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-slate-500">{mapa.observacao}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
